import { Order, OrderEvent } from 'store/orders/orders.types'
import { TripPayload, TripType } from 'store/trips/trips.types'
import { isAvailableEvent } from './trip-orders-list.utils'

export const isOrderLinkedHookTrailerEvent = (event: {
  eventType: string
  orderEventId?: number
}): boolean =>
  event.eventType === 'HOOK_TRAILER' && event.orderEventId != null

export const isOrderLinkedDropTrailerEvent = (event: {
  eventType: string
  orderEventId?: number
}): boolean =>
  event.eventType === 'DROP_TRAILER' && event.orderEventId != null

export const isOrderLinkedHookTrailerTask = (task: TripPayload): boolean =>
  task.type === 'HOOK_TRAILER' && task.orderEventId != null && !!task.orderId

/**
 * Order-sourced drop (selected from order events / exceptions).
 * Distinguished from enter-address drops which have orderId but no orderEventId yet.
 */
export const isOrderLinkedDropTrailerTask = (task: TripPayload): boolean =>
  task.type === 'DROP_TRAILER' &&
  task.orderEventId != null &&
  !!task.orderId

/** User entered drop address via "Enter Drop Trailer Address" (persisted on trip save). */
export const isEnteredDropTrailerTask = (task: TripPayload): boolean =>
  task.type === 'DROP_TRAILER' &&
  task.sourceOrderPkId != null &&
  task.orderEventId == null

/** User entered hook address via "Enter Hook Trailer Address" (persisted on trip save). */
export const isEnteredHookTrailerTask = (task: TripPayload): boolean =>
  task.type === 'HOOK_TRAILER' &&
  task.sourceOrderPkId != null &&
  task.orderEventId == null

export const isSameTripEvent = (
  task: TripPayload,
  event: OrderEvent
): boolean =>
  task.orderEventId === event.orderEventId && task.type === event.eventType

export const getAddableOrderEvents = (order: Order): OrderEvent[] =>
  [...(order.orderEvents ?? []), ...(order.exceptions ?? [])].filter(
    (event) => !!event.orderEventId && isAvailableEvent(event)
  )

export const sortOrderEventsForTrip = (events: OrderEvent[]): OrderEvent[] => {
  const typeOrder: Partial<Record<TripType, number>> = {
    PICKUP: 0,
    HOOK_TRAILER: 0,
    DELIVERY: 1,
    DROP_TRAILER: 1,
  }

  return [...events]
    .map((event, index) => ({ event, index }))
    .sort((a, b) => {
      const aOrder = typeOrder[a.event.eventType as TripType] ?? 2
      const bOrder = typeOrder[b.event.eventType as TripType] ?? 2
      if (aOrder !== bOrder) return aOrder - bOrder
      return a.index - b.index
    })
    .map(({ event }) => event)
}

/** Display-only order for Select Orders UI — does not affect trip sequence. */
export const sortOrderEventsForDisplay = (
  events: OrderEvent[]
): OrderEvent[] => {
  const typeOrder: Partial<Record<TripType, number>> = {
    PICKUP: 0,
    DROP_TRAILER: 1,
    HOOK_TRAILER: 2,
    DELIVERY: 3,
  }

  return [...events]
    .map((event, index) => ({ event, index }))
    .sort((a, b) => {
      const aOrder = typeOrder[a.event.eventType as TripType] ?? 4
      const bOrder = typeOrder[b.event.eventType as TripType] ?? 4
      if (aOrder !== bOrder) return aOrder - bOrder
      return a.index - b.index
    })
    .map(({ event }) => event)
}

export const isEventInTripTasks = (
  tripTasks: TripPayload[],
  event: OrderEvent
): boolean => tripTasks.some((task) => isSameTripEvent(task, event))
